import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, switchMap, take, tap} from 'rxjs';

import {LocalStorageService} from '../local-storage.service';
import {ServiziAttiviService} from './servizi-attivi.service';
import {
  getServizioLogicoBySlug,
  ServizioLogico,
} from '../../shared/constants/servizio-logico.const';

/**
 * Holder del Servizio attivo nella sessione — gemello di GlobalManagerComuneService,
 * ma con un compito in più: il servizio è uno stato *navigazionale* (si entra dentro),
 * quindi va anche ripristinato/auto-selezionato e tenuto coerente col comune.
 *
 * - `servizioAttivo$` / `servizioAttivo`: stato corrente (null = nessuno scelto).
 * - `setServizioAttivo()`: imposta + persiste (attivo e "ultimo usato").
 * - `reset()`: azzera lo stato e la persistenza del servizio attivo (NON l'ultimo usato).
 * - `risolviServizioAttivo$()`: ripristino all'avvio (URL → storage → ultimo usato) +
 *   invariante servizio∈comune + auto-selezione quando il comune ha 1 solo servizio.
 *
 * Lo slug è la fonte di verità persistita; il `ServizioLogico` completo si risolve dalla
 * tabella-ponte (oggi config FE, domani endpoint BE) — vedi servizio-logico.const.ts.
 */
@Injectable({providedIn: 'root'})
export class GlobalManagerServizioService {
  private readonly localStorage = inject(LocalStorageService);
  private readonly serviziAttivi = inject(ServiziAttiviService);

  servizioAttivo$ = new BehaviorSubject<ServizioLogico | null>(null);

  get servizioAttivo(): ServizioLogico | null {
    return this.servizioAttivo$.getValue();
  }

  /** Imposta il servizio attivo e lo persiste (attivo + ultimo usato). */
  setServizioAttivo(servizio: ServizioLogico | null): void {
    this.servizioAttivo$.next(servizio);
    if (servizio) {
      this.localStorage.setServizioAttivo$(servizio.slug).subscribe();
      this.localStorage.setUltimoServizio$(servizio.slug).subscribe();
    } else {
      this.localStorage.removeServizioAttivo$().subscribe();
    }
  }

  /**
   * Azzera il servizio attivo (stato + persistenza). Non tocca l'"ultimo usato". Azzera
   * anche la scelta d'istanza mensa: è il punto canonico di reset al cambio comune (chiamato
   * da comuni.page, EntraServizioService, handle404), e l'istanza appartiene a un comune →
   * non deve sopravvivere al cambio contesto (change select-servizio-post-accettazione §5.3).
   */
  reset(): void {
    this.servizioAttivo$.next(null);
    this.localStorage.removeServizioAttivo$().subscribe();
    this.serviziAttivi.resetIstanzaMensaSelezionata();
  }

  /**
   * Ripristina/auto-seleziona il servizio attivo per il comune `slugComune`.
   *
   * Ordine di risoluzione (cascata, invariante servizio∈comune). La sorgente del
   * conteggio "1 vs N" e dell'auto-selezione del mono-tipo sono i **tipi sbloccati**
   * del cittadino nel comune attivo (`ServiziAttiviService.tipiAttivi$`, asse ACCESSI),
   * NON il catalogo dei bandi aperti — change select-servizio-post-accettazione §D2:
   * un servizio appena sbloccato dev'essere selezionabile anche se il bando non è più
   * aperto. I candidati (URL/storage/ultimo) restano e sono validati contro gli sbloccati.
   *  1. `slugServizioUrl` se presente (deep-link/refresh) — solo se sbloccato.
   *  2. servizio attivo in storage — solo se sbloccato.
   *  3. ultimo servizio usato — solo se sbloccato.
   *  4. auto-selezione: se il cittadino ha 1 solo tipo sbloccato → quello.
   *  5. altrimenti null (resta da scegliere: action sheet al primo ingresso).
   *
   * `slugComune` resta nella firma per compatibilità coi chiamanti, ma la sorgente è ora
   * il comune ATTIVO (su cui `tipiAttivi$` è già filtrato): la risoluzione è invocata dopo
   * che il comune attivo è stato impostato.
   *
   * Emette su `servizioAttivo$` e ritorna il servizio risolto (o null).
   */
  risolviServizioAttivo$(
    slugComune: string,
    slugServizioUrl?: string | null,
    opts?: {soloAutoSelezione?: boolean}
  ): Observable<ServizioLogico | null> {
    return this.serviziAttivi.tipiAttivi$.pipe(
      take(1),
      switchMap((tipiSbloccati) =>
        this.leggiPreferenze$().pipe(
          tap(({attivo, ultimo}) => {
            const sbloccati = new Set(tipiSbloccati.map((s) => s.slug));

            // Su cambio comune (soloAutoSelezione) NON si ripristina da storage/ultimo:
            // ripartenza pulita, l'utente sceglie. Resta solo l'URL esplicito (deep-link)
            // e l'auto-selezione del cittadino mono-tipo.
            const candidati = opts?.soloAutoSelezione
              ? [slugServizioUrl]
              : [slugServizioUrl, attivo, ultimo];

            let scelto: ServizioLogico | null = null;
            for (const slug of candidati) {
              const servizio = getServizioLogicoBySlug(slug);
              if (servizio && sbloccati.has(servizio.slug)) {
                scelto = servizio;
                break;
              }
            }

            // Auto-selezione: cittadino con un solo tipo sbloccato → entra dritto.
            if (!scelto && tipiSbloccati.length === 1) {
              scelto = tipiSbloccati[0];
            }
            // 0 tipi sbloccati o N senza candidato valido → null (nessuna auto-selezione).

            this.setServizioAttivo(scelto);
          }),
          switchMap(() => of(this.servizioAttivo))
        )
      )
    );
  }

  private leggiPreferenze$(): Observable<{attivo: string | null; ultimo: string | null}> {
    return this.localStorage.getServizioAttivo$().pipe(
      switchMap((attivo) =>
        this.localStorage.getUltimoServizio$().pipe(
          switchMap((ultimo) => of({attivo, ultimo}))
        )
      )
    );
  }
}
